import { Link as MuiLink } from '@mui/material';
import { colorsSemantic } from '../tokens/colors-semantic.js';
import { typographyTokens } from '../tokens/typography.js';

/**
 * Link component for text links
 * Based on Figma design system, wraps MUI Link
 * 
 * Variants:
 * - primary: Gold color (default for accents)
 * - secondary: Blue color
 * - default: Standard text color
 * 
 * Can be used as button or anchor depending on props
 * 
 * @param {object} props
 * @param {'primary'|'secondary'|'default'} props.variant - Link color variant
 * @param {React.ReactNode} props.children - Link text
 * @param {function} props.onClick - Click handler (makes it a button)
 * @param {string} props.href - URL (makes it an anchor)
 * @param {boolean} props.underline - Show underline ('none', 'hover', 'always')
 * @param {object} props.sx - Additional MUI sx styles
 */
export default function Link
({
    variant = 'primary',
    children,
    onClick,
    href,
    underline = 'hover',
    sx = {},
    ...props
})
{
    const variantColors = {
        primary: colorsSemantic.text.accent.gold,
        secondary: colorsSemantic.text.accent.blue,
        default: colorsSemantic.text.default
    };

    // If onClick but no href, render as button
    const component = onClick && !href ? 'button' : 'a';
    const type = component === 'button' ? 'button' : undefined;

    return (
        <MuiLink
            component={component}
            type={type}
            href={href}
            onClick={onClick}
            underline={underline}
            sx={{
                color: variantColors[variant],
                fontSize: typographyTokens.size.base,
                fontWeight: typographyTokens.weight.semibold,
                fontFamily: typographyTokens.family.body,
                cursor: 'pointer',
                textDecoration: underline === 'always' ? 'underline' : 'none',
                border: 'none',
                background: 'none',
                padding: 0,
                '&:hover': {
                    textDecoration: underline === 'none' ? 'none' : 'underline'
                },
                ...sx
            }}
            {...props}
        >
            {children}
        </MuiLink>
    );
}
